/**
 * "Sign in with Ekselens" — Auth0 universal-login button.
 *
 * Renders only when Auth0 is configured (REACT_APP_AUTH0_DOMAIN + CLIENT_ID
 * present). Otherwise returns null so the existing local-JWT login form
 * remains the only entry point.
 *
 * Honours ?source= and ?campaign= query params for cross-property attribution.
 */
import { Button } from "@/components/ui/button";
import { LogIn } from "lucide-react";
import { isAuth0Configured, useAuth0 } from "@/lib/auth0Provider";

export function EkselensSsoButton({ className = "" }) {
  // The hook is safe to call when the provider isn't mounted because we
  // gate the render on isAuth0Configured() — but useAuth0 will throw if
  // called outside a provider, so check first.
  if (!isAuth0Configured()) return null;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { loginWithRedirect, isLoading } = useAuth0();

  const onClick = () => {
    const params = new URLSearchParams(window.location.search);
    const appState = {
      returnTo: window.location.pathname + (params.toString() ? `?${params}` : ""),
      source: params.get("source") || params.get("campaign") || null,
    };
    loginWithRedirect({ appState });
  };

  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      disabled={isLoading}
      data-testid="sso-login-btn"
      className={`w-full h-11 gap-2 border-violet-200 hover:bg-violet-50 ${className}`}
    >
      <LogIn size={16} />
      Sign in with Ekselens
    </Button>
  );
}
